import { Row, Separator, H6 } from '@fs/zion-ui'
import React, { useContext, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { getPersonChanges, getPersonContributors } from '../../../services/personService'
import { usePageTitle } from '../../app/PageTitleContext'
import { deepCopy } from '../../../util/Utils'
import { RawData } from '../../common/RawData'
import { PersonContext } from '../PersonContext'
import PersonChangesFilters from './PersonChangesFilters'
import PersonChangesTable from './PersonChangesTable'
import getTypeFiltersInitialState from '../../common/TypeFiltersState'

export default function PersonChanges() {
  usePageView({ page_detail: 'DMConsole:PersonChanges' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.changes'))
  const personId = useContext(PersonContext).currentPerson.id
  const { personChangesState, personChangesDispatch } = usePersonChanges()
  const [rerenderInfiniteScroll, setRerenderInfiniteScroll] = useState()
  /**
   * State of Filters
   */
  // tf-json-binding repository has more values but the endpoint cannot filter by them. i commented out the values that are in the tf-json-binding repository
  // but cannot be used by the endpoint because there is a tendency to look at the tf-json-binding repository and want to use them
  const filtersInitialState = getTypeFiltersInitialState({ t, type: 'person' })
  const [filtersState, setFiltersState] = useReducer(
    (oldstate, newState) => JSON.parse(JSON.stringify(newState)),
    filtersInitialState
  )

  // Get the person changes and contributors the first time we come to this page
  useEffect(() => {
    // Fetch changes immediately so user can start viewing them
    getPersonChanges(personId)
      .then((response) => {
        personChangesDispatch({
          type: 'setPersonChanges',
          state: response.data,
        })
      })
      .catch((error) => {
        console.error('Error loading person changes:', error)
      })

    // Fetch contributors and merge when ready
    getPersonContributors(personId)
      .then((response) => {
        personChangesDispatch({
          type: 'mergeIdsMetadata',
          idsMetadata: response.data,
        })
      })
      .catch((error) => {
        console.error('Error loading contributors:', error)
      })
  }, [personId, personChangesDispatch])

  return (
    <>
      <Row>
        <PersonChangesFilters
          growX
          personId={personId}
          personChangesState={personChangesState}
          personChangesDispatch={personChangesDispatch}
          setRerenderInfiniteScroll={setRerenderInfiniteScroll}
          filtersState={filtersState}
          setFiltersState={setFiltersState}
        />
        {personChangesState.changes && (
          <H6 noShrinkX>
            {t('person.changes.numberOfChanges', '{changes} changes', {
              changes: personChangesState.changes.length,
            })}
          </H6>
        )}
      </Row>
      <Separator size="xs" />
      <PersonChangesTable
        personId={personId}
        personChangesState={personChangesState}
        personChangesDispatch={personChangesDispatch}
        rerenderInfiniteScroll={rerenderInfiniteScroll}
        setRerenderInfiniteScroll={setRerenderInfiniteScroll}
        filtersState={filtersState}
      />
      <RawData state={personChangesState} />
    </>
  )
}

function usePersonChanges() {
  const { t } = useTranslation()
  function reducer(oldState, action) {
    let newState = deepCopy(oldState)
    switch (action.type) {
      case 'toggleHide':
        newState.changes
          .filter((change) => change.metadata.changeId === action.changeId)
          .forEach((change) => {
            change.metadata.hidden = action.hidden
          })
        break
      case 'toggleHideGroup':
        newState.changes
          .filter((change) => action.changeIds.includes(change.metadata.changeId))
          .forEach((change) => {
            change.metadata.hidden = action.hidden
          })
        break
      case 'setPersonChanges':
        newState = action.state
        break
      case 'mergeIdsMetadata':
        newState.idsMetadata = { ...action.idsMetadata, ...newState.idsMetadata }
        break
      case 'appendPersonChanges':
        if (action.state.changes.length > 0) {
          const lastIdFirstList = newState.changes[newState.changes.length - 1].metadata.changeId
          const firstIdSecondList = action.state.changes[0].metadata.changeId || 'noId'
          if (lastIdFirstList === firstIdSecondList) {
            newState.changes = newState.changes.concat(action.state.changes.slice(1, action.state.changes.length))
          } else {
            newState.changes = newState.changes.concat(action.state.changes)
          }
        }
        newState.idsMetadata = { ...newState.idsMetadata, ...action.state.idsMetadata }
        newState.hasMoreChanges = action.state.hasMoreChanges
        break
      default:
        console.error(
          t('person.errors.actionIsNotHandled', {
            defaultValue: `Action is not handled ${JSON.stringify(action)}`,
            message: JSON.stringify(action),
          })
        )
    }
    return newState
  }

  const [personChangesState, personChangesDispatch] = useReducer(reducer, {})

  return { personChangesState, personChangesDispatch }
}
